import { useEffect, Suspense, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { scalekitAuthService } from '@/lib/scalekit.service';

function LoadingSpinner() {
  return (
    <div className="w-12 h-12 border-4 border-amber-500/20 border-t-amber-500 rounded-full animate-spin" />
  );
}

function AuthCallbackContent() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleCallback = async () => {
      const errorParam = searchParams.get('error');
      const errorDescription = searchParams.get('error_description');

      if (errorParam) {
        console.error('Auth error:', errorParam, errorDescription);
        setError(errorDescription || errorParam);
        setTimeout(() => {
          navigate('/login?error=' + encodeURIComponent(errorDescription || errorParam));
        }, 2000);
        return;
      }

      try {
        const user = await scalekitAuthService.getCurrentUser();

        if (user) {
          const returnTo = searchParams.get('returnTo') || '/home';
          navigate(returnTo);
        } else {
          console.warn('No session established after callback');
          navigate('/login?error=Authentication+failed');
        }
      } catch (err) {
        console.error('Callback handling failed:', err);
        navigate('/login?error=Authentication+failed');
      }
    };

    handleCallback();
  }, [searchParams, navigate]);

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background-root">
        {/* Ambient background */}
        <div className="fixed inset-0 pointer-events-none overflow-hidden z-0 opacity-40">
          <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] bg-rose-500/10 rounded-full blur-[120px]" />
          <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] bg-amber-500/10 rounded-full blur-[120px]" />
        </div>

        <div className="relative z-10 text-center">
          <div className="w-16 h-16 bg-rose-500/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <svg className="w-8 h-8 text-rose-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-rose-500 mb-2">Authentication Error</h2>
          <p className="text-sm text-slate-500 dark:text-white mb-4">{error}</p>
          <p className="text-[10px] text-slate-500 dark:text-white uppercase tracking-widest">Redirecting to login...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background-root">
      {/* Ambient background */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden z-0 opacity-40">
        <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] bg-blue-500/10 rounded-full blur-[120px] animate-pulse" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] bg-amber-500/10 rounded-full blur-[120px] animate-pulse" />
      </div>

      <div className="relative z-10 text-center">
        <div className="w-16 h-16 bg-amber-500 rounded-2xl flex items-center justify-center mx-auto mb-6 text-3xl shadow-lg shadow-amber-500/30 animate-pulse">
          🐝
        </div>
        <LoadingSpinner />
        <p className="mt-6 text-sm text-slate-500 dark:text-white">Completing authentication...</p>
        <p className="mt-2 text-[10px] text-slate-500 dark:text-white uppercase tracking-widest">Please wait</p>
      </div>
    </div>
  );
}

export default function AuthCallbackPage() {
  return (
    <Suspense
      fallback={
        <div className="min-h-screen flex items-center justify-center bg-background-root">
          <LoadingSpinner />
        </div>
      }
    >
      <AuthCallbackContent />
    </Suspense>
  );
}
